/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { environment } from './Environment';
import { restClient } from './api/RestClient';
import { CompiledProblem } from './api/types';
import { Dropdown } from './supportive/Dropdown';
import { MathProblem } from './types/types2';
import { useSessionState } from './hooks/useSessionState';
import { Styled as S } from './supportive/app.styled';
import { Wikitext } from './solver/evaluation/wiki/Wikitext';
import { VerticalSolver } from './solver/VerticalSolver';
import { SolverConfig } from './solver/evaluation/context/SolverContextProvider';
import { FlexRow } from './types/Flex';
import { MAX_DELAY } from './solver/constants';

const defaultConf: SolverConfig = {
    immediateAssist: false,
    assistDelay: 0,
    allowAssistVisualization: false,
    popoverStrategy: 'cursor',
};

function App() {
    const [problems, setProblems] = useState<CompiledProblem[]>();
    const [selectedProblem, setSelectedProblem] = useSessionState<CompiledProblem>('Selected_problem', undefined);
    const [config, setConfig] = useSessionState<SolverConfig>('solver_config', defaultConf);

    useEffect(() => {
        environment.problemIds
            .reduce((p, c) => p.then((r) => restClient.getCompiledProblem(c).then((resp) => r.concat(resp))), Promise.resolve([]))
            .then((response) => setProblems(response.map((r) => JSON.parse(r.data))));
    }, []);

    useEffect(() => {
        const keyDownEvent = (e: KeyboardEvent) => {
            if (e.altKey && e.code === 'KeyA') {
                setConfig((prevState) => ({ ...prevState, allowAssistVisualization: !prevState.allowAssistVisualization }));
            }
        };
        window.addEventListener('keydown', keyDownEvent);
        return () => window.removeEventListener('keydown', keyDownEvent);
    }, []);

    const options = useMemo(() => (problems ? problems.map((p) => p.the_problem) : []), [problems]);
    const title = useMemo(() => <S.Title>{selectedProblem ? selectedProblem.the_problem.name : 'Select problem'}</S.Title>, [selectedProblem]);
    const handleSelectProblem = useCallback((problem: MathProblem) => setSelectedProblem(problems.find((cp) => cp.the_problem === problem)), [problems]);

    const handleChangeImmediateAssist = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => setConfig((prevState) => ({ ...prevState, immediateAssist: e.target.checked })),
        [setConfig]
    );

    const changePopoverStrategy = useCallback(
        (popoverStrategy: 'cursor' | 'hover') => setConfig((prevState) => ({ ...prevState, popoverStrategy })),
        [setConfig]
    );

    const handleChangeAssistDelay = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) =>
            setConfig((prevState) => {
                const value = Number.parseInt(e.target.value);
                return { ...prevState, assistDelay: value > MAX_DELAY ? MAX_DELAY : value };
            }),
        [setConfig]
    );

    return (
        <S.App>
            {problems ? (
                <>
                    <FlexRow $justifyContent={'space-between'}>
                        <Dropdown<MathProblem>
                            options={options}
                            onChange={handleSelectProblem}
                            current={selectedProblem && selectedProblem.the_problem}
                            target={title}
                        />
                        <S.Config>
                            <FlexRow $justifyContent={'space-between'}>
                                <div>Immediate assist</div>
                                <input type={'checkbox'} checked={config.immediateAssist} onChange={handleChangeImmediateAssist} />
                            </FlexRow>
                            {config.immediateAssist && (
                                <FlexRow $justifyContent={'space-between'}>
                                    <div>Assist delay</div>
                                    <label>
                                        <input
                                            type={'number'}
                                            min={0}
                                            max={MAX_DELAY}
                                            step={10}
                                            value={config.assistDelay}
                                            onChange={handleChangeAssistDelay}
                                        />
                                        ms
                                    </label>
                                </FlexRow>
                            )}
                            <fieldset>
                                <legend>Popover strategy:</legend>
                                <div>
                                    <input type="radio" onChange={() => changePopoverStrategy('cursor')} checked={config.popoverStrategy === 'cursor'} />
                                    <label>cursor</label>
                                </div>
                                <div>
                                    <input type="radio" onChange={() => changePopoverStrategy('hover')} checked={config.popoverStrategy === 'hover'} />
                                    <label>hover</label>
                                </div>
                            </fieldset>
                        </S.Config>
                    </FlexRow>
                    {selectedProblem && (
                        <>
                            <S.Statement>
                                <Wikitext wikitext={selectedProblem.the_problem.text} />
                            </S.Statement>
                            <VerticalSolver solutionId={0} problem={selectedProblem} config={config} />
                        </>
                    )}
                </>
            ) : (
                <S.Title>Fetching problems...</S.Title>
            )}
        </S.App>
    );
}

export default App;
